import { useState, useEffect, useCallback, useRef } from 'react';
import { TrackSatelliteImage } from './TrackSatelliteImage';
import { TrackLapHistogram } from './TrackLapHistogram';
import { rpc } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { trackLeaderboardSchema } from './trackLeaderboardSchema';
import { trackRaceEventsSchema } from './trackRaceEventsSchema';
import { ArrowLeft, Clock, Trophy, Calendar, MapPin } from 'lucide-react';
import { formatTime, formatSector } from '@/lib/format';
import type { ListTrackLapEntriesResponse, TrackLapEntry, ListTrackRaceEventsResponse, TrackRaceEventEntry, GetTrackLayoutResponse } from '@/types/telemetry';

const PAGE_SIZE = 10;

/** Map weather condition int to emoji */
function weatherEmoji(condition: number): string {
  const map: Record<number, string> = {
    0: '', 1: '☀️', 2: '⛅', 3: '☁️', 4: '🌫️',
    5: '🌦️', 6: '🌧️', 7: '🌧️', 8: '❄️',
    9: '🌧️', 10: '❄️', 11: '⛈️',
  };
  return map[condition] || '';
}

type TabType = 'layout' | 'measured-laps' | 'official-laps' | 'races';

interface TrackDetailProps {
  source: string;
  trackId: string;
  initialTab?: TabType;
}

export function TrackDetail({ source, trackId, initialTab }: TrackDetailProps) {
  const { params, navigate } = useRouter();
  const { t } = useI18n();

  const query = params.q || '';

  // Determine which tabs are available based on source
  const canShowMeasuredLaps = source === 'canonical' || source === 'session';
  const canShowOfficialLaps = source === 'canonical' || source === 'race_event';
  const canShowRaces = source === 'canonical' || source === 'race_event';

  // Derive tab: from URL prop, or default to layout
  const resolvedTab: TabType = initialTab
    || 'layout';

  const tab = resolvedTab;
  const page = parseInt(params.page || '1', 10);

  // Map view state from URL (layout tab only)
  const mapLat = params.lat ? parseFloat(params.lat) : undefined;
  const mapLng = params.lng ? parseFloat(params.lng) : undefined;
  const mapZoom = params.z ? parseFloat(params.z) : undefined;

  const [leaderboardData, setLeaderboardData] = useState<ListTrackLapEntriesResponse | null>(null);
  const [raceEventsData, setRaceEventsData] = useState<ListTrackRaceEventsResponse | null>(null);
  const [layoutData, setLayoutData] = useState<GetTrackLayoutResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const buildHash = (t: string, p: number, q?: string, mapState?: { lat: number; lng: number; z: number }) => {
    const encodedId = encodeURIComponent(trackId);
    let hash = `#/tracks/${source}/${encodedId}/${t}`;
    const qp = new URLSearchParams();
    if (p > 1) qp.set('page', String(p));
    if (q) qp.set('q', q);
    if (mapState) {
      qp.set('lat', String(mapState.lat));
      qp.set('lng', String(mapState.lng));
      qp.set('z', String(mapState.z));
    }
    const qs = qp.toString();
    return qs ? `${hash}?${qs}` : hash;
  };

  // Debounced URL update for map view changes
  const viewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (viewTimerRef.current) clearTimeout(viewTimerRef.current);
    };
  }, [tab]);
  const handleMapViewChange = useCallback((lat: number, lng: number, z: number) => {
    if (viewTimerRef.current) clearTimeout(viewTimerRef.current);
    viewTimerRef.current = setTimeout(() => {
      navigate(buildHash(tab, page, query || undefined, { lat, lng, z }), true);
    }, 300);
  }, [tab, page, query, source, trackId, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'layout') {
        const res = await rpc<GetTrackLayoutResponse>('GetTrackLayout', { trackId, source });
        setLayoutData(res);
        setLeaderboardData(null);
        setRaceEventsData(null);
      } else if (tab === 'races') {
        const body: Record<string, unknown> = {
          trackId,
          source,
          page,
          pageSize: PAGE_SIZE,
        };
        if (query) body.query = query;
        const res = await rpc<ListTrackRaceEventsResponse>('ListTrackRaceEvents', body);
        setRaceEventsData(res);
        setLeaderboardData(null);
        setLayoutData(null);
      } else {
        const body: Record<string, unknown> = {
          trackId,
          source,
          tab: tab === 'official-laps' ? 'official-laps' : 'measured-laps',
          page,
          pageSize: PAGE_SIZE,
        };
        if (query) body.query = query;
        const res = await rpc<ListTrackLapEntriesResponse>('ListTrackLapEntries', body);
        setLeaderboardData(res);
        setRaceEventsData(null);
        setLayoutData(null);
      }
    } catch (e: any) {
      setError(e.message || t('tracks.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [trackId, source, tab, page, query, t]);

  useEffect(() => { loadData(); }, [loadData]);

  // Navigate to a new tab (resets to page 1)
  const handleTabChange = (newTab: TabType) => {
    if (viewTimerRef.current) clearTimeout(viewTimerRef.current);
    navigate(buildHash(newTab, 1));
  };

  const handlePageChange = useCallback((newPage: number) => {
    navigate(buildHash(tab, newPage, query || undefined));
  }, [tab, source, trackId, query, navigate]);

  const totalPages = tab === 'races'
    ? (raceEventsData ? Math.ceil(raceEventsData.totalCount / PAGE_SIZE) : 0)
    : (leaderboardData ? Math.ceil(leaderboardData.totalCount / PAGE_SIZE) : 0);

  const trackName = layoutData?.trackName || raceEventsData?.trackName || leaderboardData?.trackName || trackId;

  // Redirect to include tab in URL if missing
  useEffect(() => {
    if (!initialTab) {
      navigate(buildHash(tab, 1, query || undefined), true);
    }
  }, []);

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate('#/tracks')}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
      >
        <ArrowLeft size={14} />
        {t('common.back')}
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
          {tab === 'layout' ? <MapPin size={18} className="text-accent" /> : tab === 'races' ? <Calendar size={18} className="text-accent" /> : tab === 'official-laps' ? <Trophy size={18} className="text-accent" /> : <Clock size={18} className="text-accent" />}
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{trackName}</h1>
          <p className="text-xs text-text-muted mt-0.5">{tab === 'layout' ? t('tracks.layoutTab') : tab === 'races' ? t('tracks.raceTab') : tab === 'official-laps' ? t('tracks.officialLapsTab') : t('tracks.measuredLapsTab')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto scrollbar-none">
        <button
          onClick={() => handleTabChange('layout')}
          className={`
            flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0
            ${tab === 'layout' ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
          `}
        >
          <MapPin size={13} />
          {t('tracks.layoutTab')}
        </button>
        {canShowMeasuredLaps && (
          <button
            onClick={() => handleTabChange('measured-laps')}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0
              ${tab === 'measured-laps' ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
            `}
          >
            <Clock size={13} />
            {t('tracks.measuredLapsTab')}
          </button>
        )}
        {canShowOfficialLaps && (
          <button
            onClick={() => handleTabChange('official-laps')}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0
              ${tab === 'official-laps' ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
            `}
          >
            <Trophy size={13} />
            {t('tracks.officialLapsTab')}
          </button>
        )}
        {canShowRaces && (
          <button
            onClick={() => handleTabChange('races')}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0
              ${tab === 'races' ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
            `}
          >
            <Calendar size={13} />
            {t('tracks.raceTab')}
          </button>
        )}
      </div>

      {/* Layout tab content */}
      {tab === 'layout' && (
        loading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorState message={error} onRetry={loadData} />
        ) : layoutData && (layoutData.latitude !== 0 || layoutData.longitude !== 0) ? (
          <TrackSatelliteImage
            latitude={layoutData.latitude}
            longitude={layoutData.longitude}
            bboxMinLat={layoutData.bboxMinLat}
            bboxMaxLat={layoutData.bboxMaxLat}
            bboxMinLng={layoutData.bboxMinLng}
            bboxMaxLng={layoutData.bboxMaxLng}
            viewLat={mapLat}
            viewLng={mapLng}
            viewZoom={mapZoom}
            onViewChange={handleMapViewChange}
          />
        ) : (
          <ErrorState message={t('tracks.noTrackLayout')} isNotFound />
        )
      )}

      {/* Filter toolbar (only for non-layout tabs) */}
      {tab !== 'layout' && (
        <div className="mb-4">
          <FilterBar
            schema={tab === 'races' ? trackRaceEventsSchema : trackLeaderboardSchema}
            initialQuery={query}
            onSearch={(q) => navigate(buildHash(tab, 1, q || undefined))}
            enableFilterUI={true}
          />
        </div>
      )}

      {/* Data tab content */}
      {tab !== 'layout' && (
        loading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorState message={error} />
        ) : tab === 'races' ? (
          !raceEventsData || raceEventsData.events.length === 0 ? (
            <ErrorState message={t('tracks.noRaceEvents')} isNotFound />
          ) : (
            <>
              <RaceEventsTable events={raceEventsData.events} page={page} onNavigate={navigate} />
              <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </>
          )
        ) : (
          !leaderboardData || leaderboardData.laps.length === 0 ? (
            <ErrorState message={t('tracks.noData')} isNotFound />
          ) : (
            <>
              <TrackLapHistogram histogram={leaderboardData.histogram} />
              <LeaderboardTable laps={leaderboardData.laps} tab={tab === 'official-laps' ? 'official-laps' : 'measured-laps'} page={page} onNavigate={navigate} />
              <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </>
          )
        )
      )}
    </div>
  );
}

/** Detect the maximum sector count from the lap data */
function maxSectors(laps: TrackLapEntry[]): number {
  let max = 0;
  for (const lap of laps) {
    if (lap.sector6Ms > 0) { max = 6; break; }
    if (lap.sector5Ms > 0 && max < 5) max = 5;
    if (lap.sector4Ms > 0 && max < 4) max = 4;
    if (lap.sector3Ms > 0 && max < 3) max = 3;
    if (lap.sector2Ms > 0 && max < 2) max = 2;
    if (lap.sector1Ms > 0 && max < 1) max = 1;
  }
  return max;
}

function getSector(lap: TrackLapEntry, i: number): number {
  switch (i) {
    case 1: return lap.sector1Ms;
    case 2: return lap.sector2Ms;
    case 3: return lap.sector3Ms;
    case 4: return lap.sector4Ms;
    case 5: return lap.sector5Ms;
    case 6: return lap.sector6Ms;
    default: return 0;
  }
}

function LeaderboardTable({ laps, tab, page, onNavigate }: {
  laps: TrackLapEntry[];
  tab: 'measured-laps' | 'official-laps';
  page: number;
  onNavigate: (path: string) => void;
}) {
  const { t } = useI18n();
  const isLaps = tab === 'official-laps';
  const sectorCount = isLaps ? 0 : maxSectors(laps);

  // Find the best lap time (first entry since sorted by time)
  const bestLapMs = laps.length > 0 ? laps[0].lapTimeMs : 0;

  // Find best per-sector times
  const bestSectors: number[] = [];
  for (let s = 1; s <= sectorCount; s++) {
    let best = Infinity;
    for (const lap of laps) {
      const v = getSector(lap, s);
      if (v > 0 && v < best) best = v;
    }
    bestSectors.push(best === Infinity ? 0 : best);
  }

  const thClass = "px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted";

  return (
    <div className="card overflow-hidden">
      {/* Desktop table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="divider">
              <th className={`${thClass} w-[50px]`}>#</th>
              <th className={thClass}>{t('tracks.lapTime')}</th>
              <th className={thClass}>{t('tracks.driver')}</th>
              {isLaps && <th className={thClass}>{t('tracks.category')}</th>}
              {isLaps && <th className={thClass}>{t('tracks.sessionType')}</th>}
              {isLaps && <th className={thClass}>{t('tracks.position')}</th>}
              {isLaps && <th className={thClass}>{t('tracks.season')}</th>}
              <th className={thClass}>{t('tracks.date')}</th>
              {!isLaps && Array.from({ length: sectorCount }, (_, i) => (
                <th key={i} className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">S{i + 1}</th>
              ))}
              <th className={`${thClass} w-[80px]`}>{t('tracks.weather')}</th>
            </tr>
          </thead>
          <tbody>
            {laps.map((lap, i) => {
              const rank = (page - 1) * PAGE_SIZE + i + 1;
              const isOverallBest = lap.lapTimeMs === bestLapMs && lap.lapTimeMs > 0;
              const canClick = tab === 'measured-laps' ? !!lap.sessionId && lap.lapNumber > 0
                : isLaps && !!lap.championshipId && !!lap.eventId && !!lap.categoryId;

              const handleClick = () => {
                if (tab === 'measured-laps') {
                  onNavigate(`#/session/${lap.sessionId}/lap/${lap.lapNumber}`);
                } else {
                  const qp = new URLSearchParams();
                  if (lap.sessionType) qp.set('type', lap.sessionType);
                  if (lap.driverId) qp.set('driver', lap.driverId);
                  const qs = qp.toString();
                  const base = `#/races/${lap.championshipId}/${lap.eventId}/${encodeURIComponent(lap.category)}`;
                  onNavigate(qs ? `${base}?${qs}` : base);
                }
              };

              return (
                <tr
                  key={`${lap.sessionId}-${lap.lapNumber}-${i}`}
                  onClick={canClick ? handleClick : undefined}
                  className={`
                    transition-colors
                    ${canClick ? 'cursor-pointer hover:bg-bg-hover' : ''}
                    ${i < laps.length - 1 ? 'divider' : ''}
                  `}
                >
                  <td className="px-4 py-3 text-text-muted tabular-nums mono">{rank}</td>
                  <td className={`px-4 py-3 tabular-nums mono font-medium ${isOverallBest ? 'text-purple' : 'text-text-primary'}`}>
                    {formatTime(lap.lapTimeMs)}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">{lap.driverName}</td>
                  {isLaps && (
                    <td className="px-4 py-3 text-text-secondary">
                      <span className="text-[0.65rem] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-bg-hover text-text-muted">
                        {lap.category}
                      </span>
                    </td>
                  )}
                  {isLaps && (
                    <td className="px-4 py-3 text-text-muted text-[0.65rem] capitalize">
                      {lap.sessionType.replace(/-/g, ' ')}
                    </td>
                  )}
                  {isLaps && (
                    <td className="px-4 py-3 text-text-secondary tabular-nums mono">
                      {lap.finishPosition > 0 ? `P${lap.finishPosition}` : '—'}
                    </td>
                  )}
                  {isLaps && (
                    <td className="px-4 py-3 text-text-muted tabular-nums mono">{lap.season || '—'}</td>
                  )}
                  <td className="px-4 py-3 text-text-muted tabular-nums mono">
                    {lap.date}{lap.time ? ` ${lap.time}` : ''}
                  </td>
                  {!isLaps && Array.from({ length: sectorCount }, (_, si) => {
                    const sVal = getSector(lap, si + 1);
                    const isBestSector = sVal > 0 && sVal === bestSectors[si];
                    return (
                      <td key={si} className={`px-4 py-3 tabular-nums mono ${isBestSector ? 'text-purple' : 'text-text-muted'}`}>
                        {formatSector(sVal)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-text-muted">
                    {lap.weatherCondition > 0 && (
                      <span className="flex items-center gap-1">
                        <span>{weatherEmoji(lap.weatherCondition)}</span>
                        {lap.temperatureC > 0 && <span className="tabular-nums mono">{Math.round(lap.temperatureC)}°</span>}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <div className="md:hidden">
        {laps.map((lap, i) => {
          const rank = (page - 1) * PAGE_SIZE + i + 1;
          const isOverallBest = lap.lapTimeMs === bestLapMs && lap.lapTimeMs > 0;
          const canClick = tab === 'measured-laps' ? !!lap.sessionId && lap.lapNumber > 0
            : isLaps && !!lap.championshipId && !!lap.eventId && !!lap.categoryId;

          const handleClick = () => {
            if (tab === 'measured-laps') {
              onNavigate(`#/session/${lap.sessionId}/lap/${lap.lapNumber}`);
            } else {
              const qp = new URLSearchParams();
              if (lap.sessionType) qp.set('type', lap.sessionType);
              if (lap.driverId) qp.set('driver', lap.driverId);
              const qs = qp.toString();
              const base = `#/races/${lap.championshipId}/${lap.eventId}/${encodeURIComponent(lap.category)}`;
              onNavigate(qs ? `${base}?${qs}` : base);
            }
          };

          return (
            <div
              key={`${lap.sessionId}-${lap.lapNumber}-${i}`}
              onClick={canClick ? handleClick : undefined}
              className={`
                p-4 transition-colors
                ${canClick ? 'cursor-pointer hover:bg-bg-hover' : ''}
                ${i < laps.length - 1 ? 'divider' : ''}
              `}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-text-muted text-xs mono">#{rank}</span>
                  <span className={`text-sm font-medium tabular-nums mono ${isOverallBest ? 'text-purple' : 'text-text-primary'}`}>
                    {formatTime(lap.lapTimeMs)}
                  </span>
                  {isLaps && lap.finishPosition > 0 && (
                    <span className="text-[0.65rem] text-text-muted mono">P{lap.finishPosition}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isLaps && lap.category && (
                    <span className="text-[0.6rem] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-bg-hover text-text-muted">
                      {lap.category}
                    </span>
                  )}
                  {isLaps && lap.sessionType && (
                    <span className="text-[0.6rem] capitalize text-text-muted">
                      {lap.sessionType.replace(/-/g, ' ')}
                    </span>
                  )}
                  {lap.weatherCondition > 0 && (
                    <span className="text-xs text-text-muted flex items-center gap-1">
                      {weatherEmoji(lap.weatherCondition)}
                      {lap.temperatureC > 0 && <span className="tabular-nums mono">{Math.round(lap.temperatureC)}°</span>}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-text-secondary">
                <span>{lap.driverName}</span>
                <span className="text-text-muted tabular-nums mono">{isLaps && lap.season ? `${lap.season} · ` : ''}{lap.date}</span>
              </div>
              {!isLaps && sectorCount > 0 && (
                <div className="flex items-center gap-2 mt-1.5 text-[0.65rem] text-text-muted tabular-nums mono">
                  {Array.from({ length: sectorCount }, (_, si) => {
                    const sVal = getSector(lap, si + 1);
                    const isBestSector = sVal > 0 && sVal === bestSectors[si];
                    return sVal > 0 ? (
                      <span key={si} className={isBestSector ? 'text-purple' : ''}>
                        S{si + 1}: {formatSector(sVal)}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RaceEventsTable({ events, page, onNavigate }: {
  events: TrackRaceEventEntry[];
  page: number;
  onNavigate: (path: string) => void;
}) {
  const { t } = useI18n();

  const thClass = "px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted";

  return (
    <div className="card overflow-hidden">
      {/* Desktop table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="divider">
              <th className={thClass}>{t('tracks.round')}</th>
              <th className={thClass}>{t('tracks.championship')}</th>
              <th className={thClass}>{t('tracks.season')}</th>
              <th className={thClass}>{t('tracks.dateStart')}</th>
              <th className={thClass}>{t('tracks.sessionsCount')}</th>
              <th className={thClass}>{t('tracks.driversCount')}</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event, i) => (
              <tr
                key={event.eventId}
                onClick={() => onNavigate(`#/races/${event.championshipId}/${event.eventId}`)}
                className={`
                  transition-colors cursor-pointer hover:bg-bg-hover
                  ${i < events.length - 1 ? 'divider' : ''}
                `}
              >
                <td className="px-4 py-3 text-text-secondary tabular-nums mono">R{event.roundNumber}</td>
                <td className="px-4 py-3 text-text-primary font-medium">{event.championshipName}</td>
                <td className="px-4 py-3 text-text-muted tabular-nums mono">{event.championshipYear}</td>
                <td className="px-4 py-3 text-text-muted tabular-nums mono">{event.dateStart}</td>
                <td className="px-4 py-3 text-text-muted tabular-nums mono">{event.sessionCount}</td>
                <td className="px-4 py-3 text-text-muted tabular-nums mono">{event.driverCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <div className="md:hidden">
        {events.map((event, i) => (
          <div
            key={event.eventId}
            onClick={() => onNavigate(`#/races/${event.championshipId}/${event.eventId}`)}
            className={`
              p-4 transition-colors cursor-pointer hover:bg-bg-hover
              ${i < events.length - 1 ? 'divider' : ''}
            `}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-text-muted text-xs mono">R{event.roundNumber}</span>
                <span className="text-sm font-medium text-text-primary">{event.championshipName}</span>
              </div>
              <span className="text-text-muted text-xs tabular-nums mono">{event.championshipYear}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-text-secondary">
              <span className="text-text-muted tabular-nums mono">{event.dateStart}</span>
            </div>
            <div className="flex items-center gap-3 mt-1.5 text-[0.65rem] text-text-muted">
              <span>{event.sessionCount} {t('tracks.sessionsCount').toLowerCase()}</span>
              <span>{event.driverCount} {t('tracks.driversCount').toLowerCase()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
